import { type AsAppliedLayer, asAppliedLayer, type LayerSelection } from '../data-layer-state'
import type { MapCatalog } from './catalog'
import { equivalencyKeyOf, matchAsAppliedLayer } from './equivalency'
import type { EquivalencyOutcome, LayerEquivalencyKey } from './layer-scope'

export type ResolvedLayer = { layer: LayerSelection; outcome: EquivalencyOutcome }

/** A resolution that landed in the stream branch, so its merge can still be set. */
export type ResolvedStreamLayer = { layer: AsAppliedLayer; outcome: EquivalencyOutcome }

/**
 * The key `layer` currently sits on, fetching the catalog it needs to name one.
 * Kept separate from {@link resolveStreamLayer} so the source and target
 * lookups stay independent: a source whose catalog is missing degrades to
 * "nothing to preserve" rather than aborting the move.
 */
export const equivalencyKeyFor = async (
  layer: LayerSelection,
  catalog: MapCatalog
): Promise<LayerEquivalencyKey | null> =>
  equivalencyKeyOf(
    layer,
    layer.kind === 'AsApplied' ? await catalog.applicationsFor(layer.streamId) : []
  )

/**
 * Point a stream at the layer that best honors `key`. A key from another scope
 * kind — the user was on satellite imagery and picked a pass — has no
 * counterpart here, so it resolves as `NotApplicable` rather than as a failure.
 *
 * `mergedStreamIds` is left empty: the merge belongs to the target stream's own
 * grouping, so a caller that knows one sets it on the returned layer.
 *
 * `null` means the stream carries nothing renderable, so there is no selection
 * to commit.
 */
export const resolveStreamLayer = async ({
  streamId,
  key,
  catalog
}: {
  streamId: string
  key: LayerEquivalencyKey | null
  catalog: MapCatalog
}): Promise<ResolvedStreamLayer | null> => {
  const match = matchAsAppliedLayer({
    requested: key?.kind === 'AsApplied' ? key : null,
    applications: await catalog.applicationsFor(streamId)
  })
  if (match === null) return null

  return {
    layer: asAppliedLayer({
      streamId,
      applicationId: match.applicationId,
      layerId: match.layerId
    }),
    outcome: match.outcome
  }
}
